import api from './index';
import { getUserId } from './auth';

/**
 * User Application Service
 * 
 * Manages user's trademark filing applications
 * Each application contains:
 * - Business Info
 * - Trademark
 * - Analysis
 */
export const userApplicationService = {
  /**
   * Get all applications for a user
   * @returns {Promise<Array>} List of user applications
   */
  getUserApplications: async () => {
    try {
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.get('/applications', {
        params: { userId }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user applications:', error);
      throw error;
    }
  },
  
  /**
   * Get a specific application by ID
   * @param {string} applicationId The application ID
   * @returns {Promise<Object>} The application data including business info, trademark, and analysis
   */
  getApplicationData: async (applicationId) => {
    try {
      const response = await api.get('/applications', {
        params: { applicationId }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching application ${applicationId}:`, error);
      throw error;
    }
  },
  
  /**
   * Create a new application
   * @param {string} trademarkId Optional trademark ID to associate with this application
   * @param {string} applicationName Optional name for the application
   * @returns {Promise<Object>} The new application with ID and threadId
   */
  createApplication: async (trademarkId = null, applicationName = null) => {
    try {
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('User not authenticated');
      }

      console.log('Creating application with userId:', userId, 'trademarkId:', trademarkId, 'and name:', applicationName);
      
      const payload = {
        userId,
        trademarkId, // Include trademarkId in the request
        name: applicationName // Include application name
      };

      console.log('Sending payload to /applications:', payload);
      
      const response = await api.post('/applications', payload);
      
      console.log('Received response from /applications:', response);
      console.log('Response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating new application:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },

  /**
   * Update an application with trademarkId for bidirectional linking
   * @param {string} applicationId The application ID to update
   * @param {string} trademarkId The trademark ID to link
   * @returns {Promise<Object>} The updated application
   */
  updateApplicationWithTrademark: async (applicationId, trademarkId) => {
    try {
      if (!applicationId || !trademarkId) {
        throw new Error('Application ID and Trademark ID are required');
      }

      console.log('Updating application', applicationId, 'with trademarkId:', trademarkId);
      
      // Use POST with id in body, following trademark API pattern
      const response = await api.post(`/applications`, {
        id: applicationId,
        trademarkId: trademarkId
      });
      
      console.log('Application updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating application ${applicationId} with trademark ${trademarkId}:`, error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },

  /**
   * Update an application with any data for bidirectional linking
   * @param {string} applicationId The application ID to update
   * @param {Object} updateData The data to update
   * @returns {Promise<Object>} The updated application
   */
  updateApplication: async (applicationId, updateData) => {
    try {
      if (!applicationId || !updateData) {
        throw new Error('Application ID and update data are required');
      }

      console.log('Updating application', applicationId, 'with data:', updateData);
      
      // Use POST with id in body, following trademark API pattern
      const response = await api.post(`/applications`, {
        id: applicationId,
        ...updateData
      });
      
      console.log('Application updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating application ${applicationId}:`, error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },

  /**
   * Cancel an application
   * @param {string} applicationId The application ID to cancel
   * @returns {Promise<Object>} The updated application
   */
  cancelApplication: async (applicationId) => {
    try {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      console.log('Cancelling application:', applicationId);
      
      const response = await api.post('/applications', {
        id: applicationId,
        status: 'cancelled'
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error cancelling application ${applicationId}:`, error);
      throw error;
    }
  }
};

export default userApplicationService;
